/**
 * Per-user notification utilities (boat owners, etc.).
 * Stored at userNotifications/{userId}/{notificationId}
 */
import { getDatabase, ref, push, set, update, get } from "firebase/database";
import {
  formatTimeAgo,
  isNotificationUnread,
  notifyTripEnded,
} from "./adminNotifications";

export { formatTimeAgo, isNotificationUnread };

export const OWNER_NOTIFICATION_TYPES = {
  BOAT_APPROVED: "BOAT_APPROVED",
  BOAT_REJECTED: "BOAT_REJECTED",
  BOAT_ADDED_BY_ADMIN: "BOAT_ADDED_BY_ADMIN",
  NEW_MESSAGE: "NEW_MESSAGE",
  NEW_BOOKING: "NEW_BOOKING",
  OFFER_ACCEPTED: "OFFER_ACCEPTED",
  BOOKING_PAID: "BOOKING_PAID",
  WAIVER_COMPLETED: "WAIVER_COMPLETED",
  TRIP_STARTED: "TRIP_STARTED",
  TRIP_ENDED: "TRIP_ENDED",
  TIP_RECEIVED: "TIP_RECEIVED",
  REVIEW_RECEIVED: "REVIEW_RECEIVED",
  DAMAGE_CLAIM_APPROVED: "DAMAGE_CLAIM_APPROVED",
};

export const RENTER_NOTIFICATION_TYPES = {
  NEW_MESSAGE: "NEW_MESSAGE",
  OFFER_RECEIVED: "OFFER_RECEIVED",
  BOOKING_EDIT_PROPOSED: "BOOKING_EDIT_PROPOSED",
  TRIP_ENDED: "TRIP_ENDED",
  TRIP_ISSUE: "TRIP_ISSUE",
  REVIEW_BOAT: "REVIEW_BOAT",
  PAYMENT_CONFIRMED: "PAYMENT_CONFIRMED",
  VERIFICATION_APPROVED: "VERIFICATION_APPROVED",
  VERIFICATION_REJECTED: "VERIFICATION_REJECTED",
};

export async function createUserNotification({
  userId,
  type,
  title,
  message,
  link,
}) {
  if (!userId) return null;

  const db = getDatabase();
  const newRef = push(ref(db, `userNotifications/${userId}`));
  const data = {
    type,
    title,
    message,
    read: false,
    createdAt: Date.now(),
    link: link || "/dashboard",
  };

  await set(newRef, data);
  return { id: newRef.key, ...data };
}

export function notifyOwnerBoatApproved(ownerId, listingTitle, listingId) {
  return createUserNotification({
    userId: ownerId,
    type: OWNER_NOTIFICATION_TYPES.BOAT_APPROVED,
    title: "Boat Approved",
    message: listingTitle
      ? `"${listingTitle}" has been approved and is now live.`
      : "Your boat listing has been approved.",
    link: listingId ? `/dashboard/listing/${listingId}` : "/dashboard/my-listings",
  });
}

export function notifyOwnerBoatRejected(ownerId, listingTitle, listingId) {
  return createUserNotification({
    userId: ownerId,
    type: OWNER_NOTIFICATION_TYPES.BOAT_REJECTED,
    title: "Boat Not Approved",
    message: listingTitle
      ? `"${listingTitle}" was not approved. Please review and update your listing.`
      : "Your boat listing was not approved.",
    link: listingId ? `/dashboard/listing/${listingId}` : "/dashboard/my-listings",
  });
}

export function notifyOwnerBoatAddedByAdmin(ownerId, listingTitle, listingId) {
  return createUserNotification({
    userId: ownerId,
    type: OWNER_NOTIFICATION_TYPES.BOAT_ADDED_BY_ADMIN,
    title: "New Boat Added",
    message: listingTitle
      ? `A new boat listing "${listingTitle}" was added to your account by the BBR team. Please review the details.`
      : "A new boat listing was added to your account by the BBR team. Please review the details.",
    link: listingId ? `/dashboard/listing/${listingId}` : "/dashboard/my-listings",
  });
}

export function notifyNewMessage(recipientId, senderName, senderId, messagePreview) {
  const preview =
    messagePreview != null
      ? String(messagePreview).trim().slice(0, 120)
      : "";
  const previewSuffix = preview ? `: "${preview}"` : ".";

  return createUserNotification({
    userId: recipientId,
    type: OWNER_NOTIFICATION_TYPES.NEW_MESSAGE,
    title: "New Message",
    message: senderName
      ? `You have a new message from ${senderName}${previewSuffix}`
      : `You have a new inbox message${previewSuffix}`,
    link: senderId ? `/dashboard/inbox?userId=${senderId}` : "/dashboard/inbox",
  });
}

export function notifyOwnerNewBooking(ownerId, listingTitle, bookingId) {
  return createUserNotification({
    userId: ownerId,
    type: OWNER_NOTIFICATION_TYPES.NEW_BOOKING,
    title: "New Booking",
    message: listingTitle
      ? `You have a new booking for "${listingTitle}".`
      : "You have a new booking request.",
    link: bookingId ? `/dashboard/event/${bookingId}` : "/dashboard/bookings",
  });
}

export function notifyOwnerOfferAccepted(
  ownerId,
  listingTitle,
  bookingId,
  renterName
) {
  return createUserNotification({
    userId: ownerId,
    type: OWNER_NOTIFICATION_TYPES.OFFER_ACCEPTED,
    title: "Offer Accepted",
    message: listingTitle
      ? renterName
        ? `${renterName} accepted your offer for "${listingTitle}".`
        : `A renter accepted your offer for "${listingTitle}".`
      : renterName
        ? `${renterName} accepted your offer.`
        : "A renter accepted your offer.",
    link: bookingId ? `/dashboard/event/${bookingId}` : "/dashboard/inbox",
  });
}

export function notifyOwnerBookingPaid(ownerId, listingTitle, bookingId) {
  return createUserNotification({
    userId: ownerId,
    type: OWNER_NOTIFICATION_TYPES.BOOKING_PAID,
    title: "Booking Paid",
    message: listingTitle
      ? `Payment received for "${listingTitle}".`
      : "A booking payment has been received.",
    link: bookingId ? `/dashboard/event/${bookingId}` : "/dashboard/payments",
  });
}

export function notifyOwnerWaiverCompleted(ownerId, listingTitle, bookingId) {
  return createUserNotification({
    userId: ownerId,
    type: OWNER_NOTIFICATION_TYPES.WAIVER_COMPLETED,
    title: "Waiver Completed",
    message: listingTitle
      ? `A waiver has been completed for "${listingTitle}".`
      : "A waiver has been completed for your booking.",
    link: bookingId ? `/dashboard/event/${bookingId}` : "/dashboard/bookings",
  });
}

export function notifyOwnerTripStarted(
  ownerId,
  listingTitle,
  bookingId,
  renterName
) {
  return createUserNotification({
    userId: ownerId,
    type: OWNER_NOTIFICATION_TYPES.TRIP_STARTED,
    title: "Trip Started",
    message: listingTitle
      ? renterName
        ? `Your trip for "${listingTitle}" with ${renterName} has started.`
        : `Your trip for "${listingTitle}" has started.`
      : renterName
        ? `Your trip with ${renterName} has started.`
        : "Your trip has started.",
    link: bookingId ? `/dashboard/event/${bookingId}` : "/dashboard/bookings",
  });
}

export function notifyOwnerTripEnded(
  ownerId,
  listingTitle,
  bookingId,
  renterName
) {
  return createUserNotification({
    userId: ownerId,
    type: OWNER_NOTIFICATION_TYPES.TRIP_ENDED,
    title: "Trip Ended",
    message: listingTitle
      ? renterName
        ? `Your trip for "${listingTitle}" with ${renterName} has ended.`
        : `Your trip for "${listingTitle}" has ended.`
      : renterName
        ? `Your trip with ${renterName} has ended.`
        : "Your trip has ended.",
    link: bookingId ? `/dashboard/event/${bookingId}` : "/dashboard/bookings",
  });
}

export function notifyOwnerTipReceived(
  ownerId,
  listingTitle,
  bookingId,
  tipAmountDollars,
  tipperName
) {
  const formattedAmount = Number(tipAmountDollars).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return createUserNotification({
    userId: ownerId,
    type: OWNER_NOTIFICATION_TYPES.TIP_RECEIVED,
    title: "Tip Received",
    message: listingTitle
      ? tipperName
        ? `${tipperName} left a $${formattedAmount} tip for "${listingTitle}".`
        : `You received a $${formattedAmount} tip for "${listingTitle}".`
      : tipperName
        ? `${tipperName} left you a $${formattedAmount} tip.`
        : `You received a $${formattedAmount} tip.`,
    link: bookingId ? `/dashboard/event/${bookingId}` : "/dashboard/payments",
  });
}

export function notifyOwnerDamageClaimApproved(
  ownerId,
  listingTitle,
  bookingId,
  amount,
  renterName
) {
  const formattedAmount = Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return createUserNotification({
    userId: ownerId,
    type: OWNER_NOTIFICATION_TYPES.DAMAGE_CLAIM_APPROVED,
    title: "Damage Claim Approved",
    message: listingTitle
      ? renterName
        ? `${renterName} approved your $${formattedAmount} damage claim for "${listingTitle}".`
        : `The renter approved your $${formattedAmount} damage claim for "${listingTitle}".`
      : renterName
        ? `${renterName} approved your $${formattedAmount} damage claim.`
        : `The renter approved your $${formattedAmount} damage claim.`,
    link: bookingId ? `/dashboard/event/${bookingId}` : "/dashboard/bookings",
  });
}

export function notifyOwnerReviewReceived(
  ownerId,
  listingTitle,
  bookingId,
  reviewerName,
  rating
) {
  const ratingText =
    rating && Number(rating) > 0 ? ` (${Number(rating)}★)` : "";

  return createUserNotification({
    userId: ownerId,
    type: OWNER_NOTIFICATION_TYPES.REVIEW_RECEIVED,
    title: "New Review",
    message: listingTitle
      ? reviewerName
        ? `${reviewerName} left a review for "${listingTitle}"${ratingText}.`
        : `A renter left a review for "${listingTitle}"${ratingText}.`
      : reviewerName
        ? `${reviewerName} left you a new review${ratingText}.`
        : `You received a new review${ratingText}.`,
    link: bookingId
      ? `/dashboard/event/${bookingId}`
      : "/dashboard/my-listings",
  });
}

export function isTripEndedStatus(status) {
  const normalized = (status || "").toLowerCase();
  return (
    normalized === "trip returned" ||
    normalized === "trip concluded" ||
    normalized === "trip completed" ||
    normalized === "completed"
  );
}

export async function notifyTripEndedForRenter(
  bookingId,
  { hasIssue = false } = {}
) {
  const db = getDatabase();
  const snapshot = await get(ref(db, `bookings/${bookingId}`));
  if (!snapshot.exists()) return;

  const booking = snapshot.val();
  const renterId = booking.renter;
  const ownerId = booking.owner;
  const listingTitle = booking.listingTitle;

  let renterName = "";
  if (renterId) {
    const renterSnapshot = await get(ref(db, `users/${renterId}`));
    if (renterSnapshot.exists()) {
      const renter = renterSnapshot.val();
      renterName = [renter.firstName, renter.lastName]
        .filter(Boolean)
        .join(" ");
    }
  }

  try {
    await notifyTripEnded(listingTitle, bookingId);
  } catch (err) {
    console.error("Failed to create admin trip end notification:", err);
  }

  if (ownerId) {
    try {
      await notifyOwnerTripEnded(ownerId, listingTitle, bookingId, renterName);
    } catch (err) {
      console.error("Failed to create owner trip end notification:", err);
    }
  }

  if (!renterId) return;

  await notifyRenterTripEnded(renterId, listingTitle, bookingId);

  if (hasIssue) {
    await notifyRenterTripIssue(
      renterId,
      listingTitle,
      bookingId,
      ownerId
    );
  } else {
    await notifyRenterReviewBoat(renterId, listingTitle, bookingId);
  }
}

export function notifyRenterOfferReceived(
  renterId,
  listingTitle,
  ownerId,
  isUpdate = false
) {
  return createUserNotification({
    userId: renterId,
    type: RENTER_NOTIFICATION_TYPES.OFFER_RECEIVED,
    title: isUpdate ? "Offer Updated" : "Offer Received",
    message: listingTitle
      ? `You received ${isUpdate ? "an updated offer" : "a new offer"} for "${listingTitle}".`
      : `You received ${isUpdate ? "an updated offer" : "a new offer"} from a boat owner.`,
    link: ownerId ? `/dashboard/inbox?userId=${ownerId}` : "/dashboard/inbox",
  });
}

export function notifyRenterBookingEditProposed(
  renterId,
  listingTitle,
  bookingId,
  ownerId
) {
  return createUserNotification({
    userId: renterId,
    type: RENTER_NOTIFICATION_TYPES.BOOKING_EDIT_PROPOSED,
    title: "Booking Changes Proposed",
    message: listingTitle
      ? `The boat owner proposed changes to your booking for "${listingTitle}".`
      : "The boat owner proposed changes to your booking.",
    link: bookingId
      ? `/dashboard/event/${bookingId}`
      : ownerId
        ? `/dashboard/inbox?userId=${ownerId}`
        : "/dashboard/inbox",
  });
}

export function notifyRenterTripEnded(renterId, listingTitle, bookingId) {
  return createUserNotification({
    userId: renterId,
    type: RENTER_NOTIFICATION_TYPES.TRIP_ENDED,
    title: "Trip Ended",
    message: listingTitle
      ? `Your trip for "${listingTitle}" has ended.`
      : "Your trip has ended.",
    link: bookingId ? `/dashboard/event/${bookingId}` : "/dashboard/bookings",
  });
}

export function notifyRenterTripIssue(
  renterId,
  listingTitle,
  bookingId,
  ownerId
) {
  return createUserNotification({
    userId: renterId,
    type: RENTER_NOTIFICATION_TYPES.TRIP_ISSUE,
    title: "Trip Issue Reported",
    message: listingTitle
      ? `The boat owner reported an issue for "${listingTitle}". Please review the details in your inbox.`
      : "The boat owner reported an issue on your trip. Please review the details in your inbox.",
    link: ownerId
      ? `/dashboard/inbox?userId=${ownerId}`
      : bookingId
        ? `/dashboard/event/${bookingId}`
        : "/dashboard/inbox",
  });
}

export function notifyRenterReviewBoat(renterId, listingTitle, bookingId) {
  return createUserNotification({
    userId: renterId,
    type: RENTER_NOTIFICATION_TYPES.REVIEW_BOAT,
    title: "Leave a Review",
    message: listingTitle
      ? `How was your trip on "${listingTitle}"? Leave a review for the boat owner.`
      : "How was your trip? Leave a review for the boat owner.",
    link: bookingId ? `/dashboard/event/${bookingId}` : "/dashboard/bookings",
  });
}

export function notifyRenterPaymentConfirmed(
  renterId,
  listingTitle,
  bookingId
) {
  return createUserNotification({
    userId: renterId,
    type: RENTER_NOTIFICATION_TYPES.PAYMENT_CONFIRMED,
    title: "Payment Confirmed",
    message: listingTitle
      ? `Your payment for "${listingTitle}" was successful.`
      : "Your booking payment was successful.",
    link: bookingId ? `/dashboard/event/${bookingId}` : "/dashboard/bookings",
  });
}

export function notifyRenterVerificationApproved(renterId) {
  return createUserNotification({
    userId: renterId,
    type: RENTER_NOTIFICATION_TYPES.VERIFICATION_APPROVED,
    title: "Verification Approved",
    message: "Your renter verification has been approved. You can now book boats.",
    link: "/dashboard/bookings",
  });
}

export function notifyRenterVerificationRejected(renterId) {
  return createUserNotification({
    userId: renterId,
    type: RENTER_NOTIFICATION_TYPES.VERIFICATION_REJECTED,
    title: "Verification Update",
    message:
      "Your renter verification was not approved. Please check your account for details.",
    link: "/dashboard/profile",
  });
}

export async function markUserNotificationRead(userId, id) {
  const db = getDatabase();
  const notificationRef = ref(db, `userNotifications/${userId}/${id}`);
  const snapshot = await get(notificationRef);

  if (!snapshot.exists()) {
    throw new Error("Notification not found");
  }

  await update(notificationRef, { read: true });
  return { success: true };
}

export async function markAllUserNotificationsRead(userId) {
  const db = getDatabase();
  const notificationsRef = ref(db, `userNotifications/${userId}`);
  const snapshot = await get(notificationsRef);
  const updates = {};

  if (snapshot.exists()) {
    snapshot.forEach((child) => {
      if (child.val().read !== true) {
        updates[`userNotifications/${userId}/${child.key}/read`] = true;
      }
    });
  }

  if (Object.keys(updates).length > 0) {
    await update(ref(db), updates);
  }

  return { success: true };
}

export function getUserNotificationIcon(type) {
  switch (type) {
    case OWNER_NOTIFICATION_TYPES.BOAT_APPROVED:
      return "✅";
    case OWNER_NOTIFICATION_TYPES.BOAT_REJECTED:
      return "❌";
    case OWNER_NOTIFICATION_TYPES.BOAT_ADDED_BY_ADMIN:
      return "🚤";
    case OWNER_NOTIFICATION_TYPES.NEW_MESSAGE:
    case RENTER_NOTIFICATION_TYPES.NEW_MESSAGE:
      return "💬";
    case OWNER_NOTIFICATION_TYPES.NEW_BOOKING:
    case RENTER_NOTIFICATION_TYPES.OFFER_RECEIVED:
      return "📅";
    case OWNER_NOTIFICATION_TYPES.OFFER_ACCEPTED:
      return "🤝";
    case OWNER_NOTIFICATION_TYPES.BOOKING_PAID:
    case RENTER_NOTIFICATION_TYPES.PAYMENT_CONFIRMED:
      return "💳";
    case OWNER_NOTIFICATION_TYPES.WAIVER_COMPLETED:
      return "📝";
    case OWNER_NOTIFICATION_TYPES.TRIP_STARTED:
      return "▶️";
    case OWNER_NOTIFICATION_TYPES.TRIP_ENDED:
    case RENTER_NOTIFICATION_TYPES.TRIP_ENDED:
      return "🏁";
    case OWNER_NOTIFICATION_TYPES.TIP_RECEIVED:
      return "💰";
    case OWNER_NOTIFICATION_TYPES.REVIEW_RECEIVED:
      return "⭐";
    case OWNER_NOTIFICATION_TYPES.DAMAGE_CLAIM_APPROVED:
      return "✅";
    case RENTER_NOTIFICATION_TYPES.BOOKING_EDIT_PROPOSED:
      return "✏️";
    case RENTER_NOTIFICATION_TYPES.TRIP_ISSUE:
      return "⚠️";
    case RENTER_NOTIFICATION_TYPES.REVIEW_BOAT:
      return "⭐";
    case RENTER_NOTIFICATION_TYPES.VERIFICATION_APPROVED:
      return "✅";
    case RENTER_NOTIFICATION_TYPES.VERIFICATION_REJECTED:
      return "❌";
    default:
      return "🔔";
  }
}

export function getOwnerNotificationIcon(type) {
  return getUserNotificationIcon(type);
}
